/**
 * # Sheet definitions — the unified model behind builtins and templates
 *
 * A `SheetDefinition` is a compiled-in `{title, timeScale, rows}` blueprint,
 * hydrated into an ephemeral `RawSheet` and copy-on-write promoted on first
 * edit. It is the single model behind BOTH:
 *
 *   - **builtin sheets** (sheet sidebar) — a definition that carries a
 *     reserved `lookupKey`. The key makes the sheet *singular*: it promotes
 *     under that key, so reopening returns the same persisted sheet.
 *   - **templates** (templates sidebar — demo + test) — a definition with no
 *     `lookupKey`. Each open forks a fresh `ss_<hash>` copy.
 *
 * "builtin" is therefore just "a definition with a reserved `lookupKey`": the
 * presence of `lookupKey` is the only structural difference, and the single
 * switch between singular-promote and fork-a-copy.
 *
 * ## Identity invariant (sheet id vs row ids — they reconcile DIFFERENTLY)
 *
 * The promote protocol (`client.ts` `_promote`) treats the two levels
 * asymmetrically, so the hydrator does too:
 *
 *   - **Sheet id — DERIVED, round-trips, never diverges.** Promote *sends* the
 *     sheet id and the server *preserves* it (`create_with_rows`); open is
 *     server-first. So a sheet id derived from the origin key equals the
 *     server's id by construction — there is nothing to reconcile. (Builtin
 *     singularity is enforced server-side by `lookupKey`; the uuid is just the
 *     value the server stores.) Default =
 *     `deterministicUuid(ownerScope|originKey)`; callers may override via
 *     `opts.sheetId` (e.g. templates passing `SheetId.ephemeral(templateId)`).
 *
 *     The **owner scope is load-bearing, not decoration**: `spreadsheets.id` is
 *     a GLOBAL primary key, so an id derived from the origin key alone is the
 *     same 128 bits for every user on the deployment. The first account to
 *     promote `:~lona:home` takes that row forever; every later account's
 *     promote inserts onto a squatted PK — `create_with_rows_idempotent` only
 *     reuses a sheet the CALLER owns, so the insert hits
 *     `spreadsheets_pkey`, the duplicate-recovery `GET` answers `401`, and the
 *     user's first structural edit fails with "already saved under a different
 *     account". Namespacing the seed by the signing-in principal keeps the id
 *     stable per user (a re-promote is still idempotent) while making a
 *     cross-principal id impossible to mint.
 *   - **Row ids — DERIVED but PROVISIONAL, reconciled on promote.** Promote
 *     sends NO row clientId; the server MINTS fresh ids, and for reserved rows
 *     (`:~lona:tasks`, …) it REUSES the user's existing canonical row (a
 *     per-user singleton), substituting *its* id. The client then remaps via
 *     `idRemap`/`rekeyRows`. So deterministic row ids do NOT match the server —
 *     determinism buys *pre-promote* stability (selection / deep-link survive a
 *     re-render) and concurrent-client agreement, not server agreement. The
 *     stable identity of a reserved row is its `lookupKey`, never its uuid.
 */

import type { Option } from "../core/option.ts";
import { Uuid } from "@tento-core/uuid";

import type { SheetsNetwork } from "../wire/rows-network.ts";
import type { RawCell, RawRow, RawSheet } from "../wire/raw-sheet-wire.ts";
import type { JsonObject } from "../core/json.ts";
import { fnvHex, RowId } from "../ids/row-id.ts";
import { elementAt } from "../core/checked-index.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import { SheetNetworkKey } from "../ids/sheet-network-key.ts";

/** One row in a `SheetDefinition`. Children are NESTED here; the hydrator
 *  flattens them into the `children: string[]` / `parentId` edges that
 *  `RawSheet` uses. A superset of the builtin catalog's row shape, so catalog
 *  entries are structurally `SheetDefinition`s. */
export interface SheetDefinitionRow {
  readonly type: string;
  /** Authoring handle: a stable name (e.g. a template's JSON row id). Seeds
   *  the derived row id in place of the positional path, so a row keeps its id
   *  even if siblings are reordered; doubles as the template picker key. */
  readonly name?: string;
  /** Reserved-row data address this row reconnects to on promote
   *  (`:~lona:weather`, `:~lona:tasks`, …). Omitted for pure container rows
   *  that own no reserved data of their own. */
  readonly lookupKey?: string;
  readonly label?: string;
  readonly timeScale?: string;
  readonly formula?: string;
  readonly heightPx?: number;
  readonly attributes?: JsonObject;
  readonly meta?: JsonObject;
  readonly children?: readonly SheetDefinitionRow[];
  /** Seed cells, computed at hydrate time (a function so dates can be
   *  today-relative — the definition itself is static module state; the
   *  template loader captures its anchor in this closure). */
  readonly seedCells?: () => readonly RawCell[];
}

/** A compiled-in sheet blueprint. `lookupKey` present ⇒ builtin (singular,
 *  promote-under-key); absent ⇒ template (fresh copy per open). */
export interface SheetDefinition {
  readonly lookupKey?: string;
  readonly title: string;
  readonly width?: number;
  readonly timeScale?: SheetsNetwork.TimeScale;
  readonly description?: string;
  readonly rows: readonly SheetDefinitionRow[];
}

/** How `hydrateSheetDefinition` mints a definition's ids: the stable origin key they derive from. */
export interface HydrateOptions {
  /** Stable origin key the ids are derived from — the builtin's reserved
   *  `lookupKey` (sheet key) or a template's id. Seeds the (derived) sheet id
   *  and namespaces every row id, so two hydrations of the same definition are
   *  reproducible. */
  readonly originKey: string;
  /**
   * The principal that will own the promoted sheet — the signed-in user id
   * (`RowLocalId.Registry.currentUser`). Namespaces the derived sheet id so two
   * accounts hydrating the same builtin never mint the same global
   * `spreadsheets.id` (see the "Identity invariant" above).
   *
   * A client passes its registry's `ownerScope` (`RowLocalId.Registry`),
   * which before login is the client's own `session:<uuid>` scope — so an
   * unknown-principal hydration agrees with itself inside the client and
   * never collides with another client's; an unknown principal MUST NOT reuse
   * a constant, which is exactly the squat this scope prevents. `null` only
   * with an explicit `sheetId` (the scope then seeds nothing); a derived id
   * with no scope throws.
   */
  readonly ownerScope: Option<string>;
  /** Explicit sheet id override. Default =
   *  `deterministicUuid(ownerScope|originKey)`, which round-trips through
   *  promote (the server preserves the sent sheet id). Templates may pass
   *  `SheetId.ephemeral(templateId)` here to keep their established
   *  `ss_<hash>` form. */
  readonly sheetId?: SheetNetworkKey;
}

/**
 * Deterministic SHEET id from a stable seed — a dashed-UUID (a valid bare
 * `SheetId` that canonicalizes to `ss_<hex>`). Same seed → same id; it
 * round-trips through promote (the server preserves the sent sheet id), so a
 * builtin's sheet id never diverges. Templates override via `opts.sheetId`
 * (`SheetId.ephemeral(templateId)`) from the app layer — this package cannot
 * import `SheetId` (the parent `@tento/lona` imports FROM here). Row ids use
 * `RowId.deterministic` (`r_<hex>`); both share `fnvHex` so they stay in lockstep.
 */
function deterministicSheetId(seed: string): string {
  const hex = fnvHex(seed);
  return (
    `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
    `${hex.slice(16, 20)}-${hex.slice(20, 32)}`
  );
}

/**
 * Sheet id for a hydration, namespaced to the principal that will own the
 * promoted sheet. `spreadsheets.id` is a global PK — see the "Identity
 * invariant" docblock for why the scope is what keeps one account's promote off
 * another account's row.
 */
function ownedSheetId(ownerScope: Option<string>, originKey: string): string {
  if (ownerScope == null) {
    throw new TypeError(
      `hydrateSheetDefinition: "${originKey}" derives its sheet id, which needs an ownerScope ` +
        "(`RowLocalId.Registry.ownerScope`: the signed-in user or the client's session scope) " +
        "or an explicit sheetId",
    );
  }
  return deterministicSheetId(`${ownerScope}|${originKey}`);
}

/**
 * Hydrate a `SheetDefinition` into an ephemeral `RawSheet`. Nested `children`
 * are flattened into the `children: string[]` / `parentId` edges
 * `RawSheet.hydrate` expects. Both the sheet id and the row ids are derived
 * deterministically from `opts.originKey` — see the "Identity invariant" above
 * for how each reconciles with the server on promote (sheet id round-trips;
 * row ids are provisional and remapped).
 *
 * Only the SHEET id takes `opts.ownerScope`. Row ids deliberately do not: they
 * are never sent to the server (promote omits `clientId`, the server mints), so
 * they never enter a global namespace where a cross-account collision could
 * matter — and keeping them origin-keyed preserves the stable pre-promote
 * selection / deep-link identity the invariant above describes.
 */
export function hydrateSheetDefinition(
  def: SheetDefinition,
  opts: HydrateOptions,
): RawSheet {
  const rawRows: RawRow[] = [];
  const idForPath = (path: string, row: SheetDefinitionRow): string =>
    RowId.deterministic(
      `${opts.originKey}|${row.name ?? path}|${row.type}|${row.lookupKey ?? ""}`,
    ).toString();
  const appendRows = (
    rows: readonly SheetDefinitionRow[],
    parentId: Option<string>,
    parentPath: string,
  ): void => {
    for (let i = 0; i < rows.length; i++) {
      const row = elementAt(rows, i, "sheet-definition: rows");
      const path = parentPath === "" ? `${i}` : `${parentPath}.${i}`;
      const id = idForPath(path, row);
      const childRows = row.children ?? [];
      const childIds = childRows.map((child, j) =>
        idForPath(`${path}.${j}`, child)
      );
      const cells = row.seedCells?.();
      rawRows.push({
        id: RowNetworkKey.fromWire(id),
        type: row.type,
        ...(row.label !== undefined ? { label: row.label } : {}),
        ...(row.lookupKey !== undefined ? { lookupKey: row.lookupKey } : {}),
        ...(row.timeScale !== undefined ? { timeScale: row.timeScale } : {}),
        ...(row.formula !== undefined ? { formula: row.formula } : {}),
        ...(row.heightPx !== undefined ? { heightPx: row.heightPx } : {}),
        ...(parentId != null ? { parentId: RowNetworkKey.fromWire(parentId) } : {}),
        ...(childIds.length > 0 ? { children: childIds.map(RowNetworkKey.fromWire) } : {}),
        ...(row.attributes !== undefined ? { attributes: row.attributes } : {}),
        ...(row.meta !== undefined ? { meta: row.meta } : {}),
        ...(cells !== undefined ? { cells } : {}),
      });
      appendRows(childRows, id, path);
    }
  };
  appendRows(def.rows, null, "");
  return {
    id: opts.sheetId ?? SheetNetworkKey.fromWire(ownedSheetId(opts.ownerScope, opts.originKey)),
    title: def.title,
    // The reserved key is what makes this definition a BUILTIN, so it rides on
    // the hydrated sheet rather than living only in the caller's hand: the
    // promoted copy comes back addressed by its server id, and every consumer
    // that restores builtin shape (row `lookupKey` / `heightPx`, sheet `width`)
    // keys off this. A template carries none and stays a plain sheet.
    ...(def.lookupKey != null ? { lookupKey: def.lookupKey } : {}),
    ...(def.width != null ? { width: def.width } : {}),
    ...(def.timeScale != null ? { timeScale: def.timeScale } : {}),
    rows: rawRows,
  };
}
